/**
 * Assign Risk Level Dialog Component
 * Displays a confirmation dialog for bulk assigning risk levels to customers
 * Shows counts of risk level changes
 */

import React from "react";
import { AlertCircle, CheckCircle2, Users, TrendingUp } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

interface Customer {
  id: string;
  name: string;
  accountNumber: string;
  currency: "USD" | "EUR" | "GBP" | "CAD" | "AUD";
  outstanding: number;
  overdue: number;
  creditLimit: number;
  dso: number;
  risk: RiskValue;
  lastPayment: string;
  contact: string;
  phone: string;
  address: string;
  paymentTerms: "Net 15" | "Net 30" | "Net 45" | "Net 60" | "Net 90";
  collectionStrategyId: string;
}

type RiskValue = "high" | "medium" | "low";

interface RiskLevel {
  id: string;
  label: string;
  value: "high" | "medium" | "low";
  description: string;
  color: string;
}

interface AssignRiskLevelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCustomers: Customer[];
  onConfirm: (riskLevel: "high" | "medium" | "low") => void;
}

const riskLevels: RiskLevel[] = [
  {
    id: "high",
    label: "High Risk",
    value: "high",
    description: "Customers with significant payment delays or high exposure",
    color: "text-red-600 dark:text-red-500",
  },
  {
    id: "medium",
    label: "Medium Risk",
    value: "medium",
    description: "Customers with moderate payment concerns",
    color: "text-amber-600 dark:text-amber-500",
  },
  {
    id: "low",
    label: "Low Risk",
    value: "low",
    description: "Customers with good payment history",
    color: "text-green-600 dark:text-green-500",
  },
];

export function AssignRiskLevelDialog({
  open,
  onOpenChange,
  selectedCustomers,
  onConfirm,
}: Readonly<AssignRiskLevelDialogProps>) {
  const [selectedRiskLevel, setSelectedRiskLevel] = React.useState<string>("");

  // Calculate counts by current risk level
  const totalSelected = selectedCustomers.length;
  const riskBreakdown = {
    high: selectedCustomers.filter((c) => c.risk === "high").length,
    medium: selectedCustomers.filter((c) => c.risk === "medium").length,
    low: selectedCustomers.filter((c) => c.risk === "low").length,
  };

  // Get selected risk level details
  const selectedRisk = riskLevels.find((r) => r.value === selectedRiskLevel);

  // Calculate how many will change
  const willChange = selectedRiskLevel
    ? selectedCustomers.filter((c) => c.risk !== selectedRiskLevel).length
    : 0;
  const willRemainSame = totalSelected - willChange;

  const handleConfirm = () => {
    if (selectedRiskLevel) {
      onConfirm(selectedRiskLevel as "high" | "medium" | "low");
      setSelectedRiskLevel("");
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    setSelectedRiskLevel("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <TrendingUp className="h-5 w-5 text-primary" />
            Assign Risk Level
          </DialogTitle>
          <DialogDescription>
            Select a risk level to assign to the selected customer accounts.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Risk Level Selection */}
          <div className="space-y-2">
            <Label htmlFor="risk-select">Risk Level</Label>
            <Select value={selectedRiskLevel} onValueChange={setSelectedRiskLevel}>
              <SelectTrigger id="risk-select">
                <SelectValue placeholder="Select a risk level..." />
              </SelectTrigger>
              <SelectContent>
                {riskLevels.map((risk) => (
                  <SelectItem key={risk.id} value={risk.value}>
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${risk.color}`}>{risk.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedRisk && (
              <p className="text-sm text-muted-foreground mt-2">
                {selectedRisk.description}
              </p>
            )}
          </div>

          <Separator />

          {/* Impact Summary */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
              <h4 className="font-semibold text-sm">Impact Summary</h4>
            </div>

            <div className="grid gap-3">
              {/* Total Selected */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Total Selected</span>
                </div>
                <Badge variant="secondary" className="text-base font-semibold">
                  {totalSelected}
                </Badge>
              </div>

              {/* Current Risk Breakdown */}
              <div className="p-3 rounded-lg bg-muted/30 border border-muted">
                <div className="text-sm font-medium mb-2">Current Risk Distribution</div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="flex flex-col items-center p-2 rounded bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900">
                    <span className="text-xs text-red-700 dark:text-red-400">High</span>
                    <span className="text-lg font-bold text-red-900 dark:text-red-100">{riskBreakdown.high}</span>
                  </div>
                  <div className="flex flex-col items-center p-2 rounded bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900">
                    <span className="text-xs text-amber-700 dark:text-amber-400">Medium</span>
                    <span className="text-lg font-bold text-amber-900 dark:text-amber-100">{riskBreakdown.medium}</span>
                  </div>
                  <div className="flex flex-col items-center p-2 rounded bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900">
                    <span className="text-xs text-green-700 dark:text-green-400">Low</span>
                    <span className="text-lg font-bold text-green-900 dark:text-green-100">{riskBreakdown.low}</span>
                  </div>
                </div>
              </div>

              {/* Changes Preview */}
              {selectedRiskLevel && willChange > 0 && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-500" />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-green-900 dark:text-green-100">
                        Risk Level Changes
                      </span>
                      <span className="text-xs text-green-700 dark:text-green-400">
                        Customers that will be updated to {selectedRisk?.label}
                      </span>
                    </div>
                  </div>
                  <Badge className="bg-green-600 hover:bg-green-700 text-white text-base font-semibold">
                    {willChange}
                  </Badge>
                </div>
              )}

              {/* No Changes */}
              {selectedRiskLevel && willRemainSame > 0 && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-500" />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Already at Selected Level
                      </span>
                      <span className="text-xs text-blue-700 dark:text-blue-400">
                        Customers already marked as {selectedRisk?.label}
                      </span>
                    </div>
                  </div>
                  <Badge className="bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold">
                    {willRemainSame}
                  </Badge>
                </div>
              )}
            </div>

            {/* Information Note */}
            <div className="flex gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900">
              <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-900 dark:text-blue-100">
                <strong>Note:</strong> This action will immediately assign the selected risk level 
                to all {totalSelected} customer{totalSelected !== 1 ? "s" : ""}. 
                {willChange > 0 && (
                  <> {willChange} customer{willChange !== 1 ? "s" : ""} will have their risk level updated.</>
                )}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedRiskLevel}
            className="gap-2"
          >
            <CheckCircle2 className="h-4 w-4" />
            Confirm Assignment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
